import { useCallback, useEffect, useState } from 'react';
import {
  ApiError,
  getShippingFees,
  type ApiClient,
  type ShippingFees,
  type ShippingFeesParams,
} from '../api';

/** Why a live quote couldn't be produced, so the UI can react correctly:
 *  - `rating_unavailable` — the upstream rating provider timed out / errored
 *    (HTTP 503 `SHIPPING_RATING_UNAVAILABLE`). Transient → offer a retry.
 *  - `unrateable_address` — the address is valid but has no shipping rate
 *    (HTTP 422 `SHIPPING_UNRATEABLE_ADDRESS`). Prompt the player to fix it;
 *    a retry of the same address won't help.
 *  - `unknown` — any other failure (network, unexpected 5xx). Offer a retry. */
export type ShippingFeesError = 'rating_unavailable' | 'unrateable_address' | 'unknown';

export interface UseShippingFeesResult {
  fees: ShippingFees | null;
  /** True while a quote is in flight — including the debounce window — so the
   *  UI can show loaders in place of the numbers. */
  loading: boolean;
  /** Set when the last quote failed (and `fees` is cleared so a stale price is
   *  never shown for inputs that couldn't be rated); null on success. */
  error: ShippingFeesError | null;
  /** Re-run the quote with the same inputs — for the transient retry button. */
  retry: () => void;
}

// As the delivery page settles, the item set, the address and the chosen
// service each flip a dependency a few ms apart — without debouncing that
// fires three identical requests. Wait for the inputs to stop changing, then
// fetch once.
const DEBOUNCE_MS = 250;

/**
 * Map a fees-endpoint failure onto a UI-actionable reason. The backend returns
 * `503 SHIPPING_RATING_UNAVAILABLE` (transient upstream — retry) and
 * `422 SHIPPING_UNRATEABLE_ADDRESS` (fix the address); anything else is
 * `'unknown'`. Reads BOTH the error code and the HTTP status so either alone
 * still classifies (the client surfaces both on `ApiError`).
 */
export function classifyShippingFeesError(e: unknown): ShippingFeesError {
  if (e instanceof ApiError) {
    if (e.code === 'SHIPPING_RATING_UNAVAILABLE' || e.status === 503) return 'rating_unavailable';
    if (e.code === 'SHIPPING_UNRATEABLE_ADDRESS' || e.status === 422) return 'unrateable_address';
  }
  return 'unknown';
}

/**
 * Fetches the pre-checkout shipping fees for the given items — debounced, and
 * re-quoted whenever the ids, the delivery address, or the chosen shipping
 * options actually change. `params.addressId` unlocks the shipping quote and
 * `params.serviceIds` the per-option prices; omit them (reveal / disclaimer)
 * and shipping stays "TBD" (a `200` with null shipping — never an error).
 *
 * Returns `{ fees, loading, error, retry }`. On a rating failure `fees` is
 * cleared and `error` is set to the reason so the caller can show a retry
 * ('rating_unavailable' / 'unknown') or an address prompt ('unrateable_address')
 * instead of a silent/stale price.
 */
export function useShippingFees(
  client: ApiClient,
  poolItemIds: (number | string)[],
  params: ShippingFeesParams = {},
): UseShippingFeesResult {
  const [fees, setFees] = useState<ShippingFees | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ShippingFeesError | null>(null);
  // Bumped by retry() to force a re-fetch with the SAME inputs.
  const [reloadNonce, setReloadNonce] = useState(0);

  const retry = useCallback(() => setReloadNonce((n) => n + 1), []);

  // Stable dependencies: re-fetch only when the actual values change, not on
  // every new array/object identity a parent render produces.
  const key = poolItemIds.join(',');
  const addressId = params.addressId ?? null;
  const serviceKey = (params.serviceIds ?? []).join(',');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const timer = setTimeout(() => {
      const ids = key ? key.split(',') : [];
      const serviceIds = serviceKey ? serviceKey.split(',') : [];
      void getShippingFees(client, ids, { addressId, serviceIds })
        .then((result) => {
          if (cancelled) return;
          setFees(result);
          setError(null);
          setLoading(false);
        })
        .catch((e) => {
          if (cancelled) return;
          // A failed quote must not look like a valid one: clear `fees` and
          // surface the reason so the UI shows a retry / address prompt.
          setFees(null);
          setError(classifyShippingFeesError(e));
          setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [client, key, addressId, serviceKey, reloadNonce]);

  return { fees, loading, error, retry };
}
